import { useEffect } from 'react';

// Dependency-free per-page SEO / social-meta helper.
//
// React 19 has peer-dep friction with react-helmet, so instead of pulling in a
// library we just poke the DOM directly. On mount / when the inputs change this
// sets document.title and creates-or-updates the description meta plus the
// og:* and twitter:* tags. We deliberately DON'T restore previous values on
// unmount — SPA navigation just re-runs this on the next page and overwrites.
//
// CRAWLER CAVEAT: Facebook / iMessage / most link-preview bots do NOT run JS,
// so they only ever see the STATIC tags in public/index.html (one shared
// site-wide preview). This hook still helps Google (it renders JS) and fixes
// the browser tab title on every route. True per-product social previews would
// need server-side prerendering, which is not set up here.

const SITE_NAME = 'Shiny Vault LGS';

// Find-or-create a <meta> tag keyed by name= or property=, set its content.
function setMeta(attr, key, content) {
  if (content == null) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

export function useSeo({ title, description, image, url } = {}) {
  useEffect(() => {
    if (title) document.title = title;

    if (description) {
      setMeta('name', 'description', description);
      setMeta('property', 'og:description', description);
      setMeta('name', 'twitter:description', description);
    }

    if (title) {
      setMeta('property', 'og:title', title);
      setMeta('name', 'twitter:title', title);
    }

    if (url) {
      setMeta('property', 'og:url', url);
      // Keep the canonical link in sync too.
      let canonical = document.head.querySelector('link[rel="canonical"]');
      if (!canonical) {
        canonical = document.createElement('link');
        canonical.setAttribute('rel', 'canonical');
        document.head.appendChild(canonical);
      }
      canonical.setAttribute('href', url);
    }

    if (image) {
      setMeta('property', 'og:image', image);
      setMeta('name', 'twitter:image', image);
    }

    setMeta('property', 'og:site_name', SITE_NAME);
  }, [title, description, image, url]);
}
